import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import type { CrewMembershipDetail } from "@/types/crew"

/**
 * OKS-26 — Notes editor for the creator detail page (UX Spec V2 §Notes
 * card). Port-adapted from OKS-18 `<CandidateNotesEditor>`; differences:
 *
 *   - Wire form-namespace is **`membership[notes]`** (NOT
 *     `candidate[notes]`). BE strong-params:
 *     `params.require(:membership).permit(:notes, :engagement_rate, tags: [])`
 *   - PATCH target is **BE-supplied `actions.update_url`**, NOT a
 *     js-routes helper reconstructed on the FE. Single-source for the
 *     URL avoids drift if BE ever moves the controller.
 *   - 4 000 char hard cap (BE CHECK + model validation). Counter
 *     surfaces from 3 500 onward with `aria-live="polite"` (avoids
 *     SR spam at every keystroke).
 *   - Errors keyed `errors["membership.notes"]` per BE rev-5 (nested
 *     namespace flattens through Inertia's error envelope).
 *
 * **Crew form rules respected:** plain shadcn `Textarea` + `Label` +
 * Inertia `<Form>` render-prop. **No shadcn Form / FormField / RHF /
 * zod.**
 *
 * **Wire-format canary** (Vitest test asserts literal `name` attr —
 * project memory `feedback_vitest_wire_format_canary`): textarea
 * `name="membership[notes]"`. Top-level `notes` would trip
 * `params.require(:membership)` → silent failure.
 *
 * Read-only mode (`!can_edit`): renders the notes as muted text in a
 * card; no form, no inputs.
 *
 * Last-edited stamp surfaces above the editor when populated.
 *
 * Cancel resets the textarea to the server value (clears local typing
 * state). Save button is disabled while content matches the server
 * value (no-op submit prevention).
 *
 * Locked testids:
 *   - `creator-detail-notes-section` — Card wrapper
 *   - `creator-detail-notes-form` — `<form>` element
 *   - `creator-detail-notes-input` — Textarea
 *   - `creator-detail-notes-submit` — Save button
 *   - `creator-detail-notes-cancel` — Cancel button
 *   - `creator-detail-notes-stamp` — Last-edited line (when populated)
 *   - `creator-detail-notes-error` — Error region
 *   - `creator-detail-notes-counter` — Char counter (>3 500 only)
 *   - `creator-detail-notes-readonly` — Read-only display
 */

const NOTES_MAX_LENGTH = 4000
const NOTES_COUNTER_THRESHOLD = 3500

interface CrewMemberNotesEditorProps {
  membership: Pick<
    CrewMembershipDetail,
    "notes" | "notes_updated_by" | "notes_updated_at"
  >
  /** BE-supplied PATCH target — `actions.update_url`. Always non-null when canEdit. */
  updateUrl: string
  canEdit: boolean
}

export function CrewMemberNotesEditor({
  membership,
  updateUrl,
  canEdit,
}: CrewMemberNotesEditorProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const serverValue = membership.notes ?? ""
  const [value, setValue] = useState(serverValue)
  const dirty = value !== serverValue
  const counterVisible = value.length > NOTES_COUNTER_THRESHOLD

  const stamp = renderLastEdited(t, membership)

  return (
    <Card data-testid="creator-detail-notes-section">
      <CardHeader>
        <CardTitle>{t("crew.detail.notes.section_title")}</CardTitle>
        {stamp && (
          <p
            data-testid="creator-detail-notes-stamp"
            className="text-muted-foreground text-xs"
          >
            {stamp}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {!canEdit && (
          <p
            data-testid="creator-detail-notes-readonly"
            className="text-muted-foreground bg-muted/30 rounded-md border p-3 text-sm whitespace-pre-line"
          >
            {serverValue.trim() || t("crew.detail.notes.readonly_label")}
          </p>
        )}
        {canEdit && (
          <Form
            action={updateUrl}
            method="patch"
            data-testid="creator-detail-notes-form"
            options={{ preserveScroll: true }}
          >
            {({ errors, processing }) => (
              <div className="flex flex-col gap-3">
                <Label htmlFor="creator-notes-input" className="sr-only">
                  {t("crew.detail.notes.section_title")}
                </Label>
                <Textarea
                  id="creator-notes-input"
                  // **Wire-format canary** — `membership[notes]` nests under
                  // BE's `params.require(:membership)`. NEVER `notes` at the
                  // top level (would 400 silently). See module doc.
                  name="membership[notes]"
                  rows={5}
                  maxLength={NOTES_MAX_LENGTH}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder={t("crew.detail.notes.placeholder")}
                  disabled={processing}
                  aria-describedby="creator-notes-error"
                  data-testid="creator-detail-notes-input"
                  className="resize-y"
                />
                {counterVisible && (
                  <p
                    aria-live="polite"
                    data-testid="creator-detail-notes-counter"
                    className="text-muted-foreground self-end text-xs tabular-nums"
                  >
                    {t("crew.detail.notes.counter_format", {
                      count: value.length,
                      limit: NOTES_MAX_LENGTH,
                    })}
                  </p>
                )}
                <div
                  id="creator-notes-error"
                  data-testid="creator-detail-notes-error"
                  role="alert"
                >
                  {/*
                    Inertia flattens nested params errors with dot keys:
                    `errors["membership.notes"]`. BE rev-5 lock.
                  */}
                  <InputError messages={errors["membership.notes"]} />
                </div>
                {!online && (
                  <p className="text-muted-foreground text-xs" role="status">
                    {t("crew.detail.notes.offline_disabled")}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={!dirty || processing}
                    onClick={() => setValue(serverValue)}
                    data-testid="creator-detail-notes-cancel"
                  >
                    {t("crew.detail.notes.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!dirty || processing || !online}
                    data-testid="creator-detail-notes-submit"
                  >
                    {processing
                      ? t("crew.detail.notes.saving")
                      : t("crew.detail.notes.save")}
                  </Button>
                </div>
              </div>
            )}
          </Form>
        )}
      </CardContent>
    </Card>
  )
}

function renderLastEdited(
  t: ReturnType<typeof useTranslation>["t"],
  membership: Pick<
    CrewMembershipDetail,
    "notes_updated_by" | "notes_updated_at"
  >,
): string | null {
  if (!membership.notes_updated_by || !membership.notes_updated_at) return null
  const when = new Date(membership.notes_updated_at)
  return t("crew.detail.notes.last_edited_by", {
    by: membership.notes_updated_by.name,
    when: when.toLocaleString(),
  })
}
